import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, type MenuItems } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import type { ChildrenProps } from "@msuite/ui/types";
import { openGoogleMaps } from "@msuite/utils/misc";
import type { Nullable, ProjectCard } from "@msuite/utils/types";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { AddTicketModal } from "@/modals/projects/add-ticket-modal";
import { EditProjectModal } from "@/modals/projects/edit-project-modal";

/** Props Interface */
interface ProjectCardContextMenuProps extends ChildrenProps {
	projectId: Nullable<string>;
	project?: Nullable<
		Omit<ProjectCard, "_tickets" | "createdAt" | "updatedAt"> & {
			createdAt: string | number | null;
			updatedAt: string | number | null;
		}
	>;
	items?: MenuItems;
}

export function ProjectCardContextMenu({
	children,
	projectId,
	project,
	items,
}: ProjectCardContextMenuProps) {
	/** Hooks */
	const addTicketModal = useDisclosure();
	const editProjectModal = useDisclosure();
	const asyncModal = useAsyncModal();
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { createIsDenied } = useTicketRules();

	/** Computed */
	// The card names the site the new ticket would land on, so the rule binds against it: a role
	// that may raise tickets only on the sites its holder runs offers this on those cards alone.
	const cannotCreateTicket = createIsDenied(projectId ?? null, project?.orgId);

	/** Query */
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnDeactivateProject() {
		await asyncModal.confirm();
	}

	async function handleOnRefetchProjects() {
		if (!projectId) return;
		await qc.invalidateQueries({
			predicate: invalidate([projectId]),
		});
	}

	function handleOnNavigateToProject() {
		window.open(`/projects/${projectId}`, "_blank");
	}

	/** Computed */
	// The card carries the project it is about, so the rule binds against the row rather than the
	// category — `Project` reaches the whole organisation, the sites the caller runs, or the ones
	// they created. Same three-state guard as `Can` documents: an entry disappears on a definitive
	// denial only, and a card rendered without its project falls back to the category check.
	// A project of another organisation is refused outright: the admin wildcard carries no
	// condition, so the rule alone would let a partner's admin edit the host's project.
	const updateIsDenied =
		!!citadel &&
		(citadel.isForeignOrganization(project?.orgId) ||
			citadel.cannot("update", "Project", project ? { ...project } : undefined));

	/** Render */
	return (
		<React.Fragment>
			<Menu
				type="context-menu"
				items={() => [
					{
						label: t("general.information"),
						icon: icon("Info"),
						onClick: handleOnNavigateToProject,
					},
					project
						? {
								label: t("maps.open_google_maps"),
								icon: icon("Map"),
								onClick: () => {
									openGoogleMaps(project);
								},
							}
						: null,
					"divider",
					...(items || []),
					updateIsDenied
						? null
						: {
								label: t("projects.edit_project"),
								icon: icon("Edit"),
								onClick: editProjectModal.onOpen,
							},
					cannotCreateTicket
						? null
						: {
								label: t("projects.add_ticket"),
								icon: icon("Plus"),
								onClick: addTicketModal.onOpen,
							},
					updateIsDenied ? null : "divider",
					updateIsDenied
						? null
						: {
								label: t("projects.deactivate_project"),
								icon: icon("Archive"),
								onClick: handleOnDeactivateProject,
								isDanger: true,
							},
				]}
			>
				{children}
			</Menu>
			{projectId && (
				<ModalWrapper>
					{editProjectModal.isOpen && (
						<EditProjectModal
							isOpen={editProjectModal.isOpen}
							onClose={editProjectModal.onClose}
							projectId={projectId}
							onComplete={handleOnRefetchProjects}
						/>
					)}
					{addTicketModal.isOpen && (
						<AddTicketModal
							isOpen={addTicketModal.isOpen}
							onClose={addTicketModal.onClose}
							projectId={projectId}
						/>
					)}
					{asyncModal.isOpen && (
						<ConfirmModal
							controller={asyncModal}
							message={t("confirmation.confirm_deactivation")}
						/>
					)}
				</ModalWrapper>
			)}
		</React.Fragment>
	);
}
